import React, { useState, useRef, useCallback } from "react";
import L from "leaflet";
import * as turf from "@turf/turf";
import "./App.css";

import MapComponent from "./components/MapComponent";
import TopBar from "./components/TopBar";
import LayerControl from "./components/LayerControl";
import ActivatorIcons from "./components/ActivatorIcons";
import Toolbar from "./components/Toolbar";
import Chatbot from "./components/Chatbot";
import LoadingOverlay from "./components/LoadingOverlay";
import AnalysisLegend from "./components/AnalysisLegend";
import AnalysisDashboard from "./components/AnalysisDashboard";
import { RadiusModal, ClearModal } from "./components/Modals";

import { fetchOSMData, getPolygonFilter } from "./services/osmService";
import { collectAreaData } from "./services/dataCollectionService";
import { analyzeWithGPT, askGPT } from "./services/gptService";

function App() {
  const [activeLayer, setActiveLayer] = useState("street");
  const [toolbarVisible, setToolbarVisible] = useState(false);
  const [chatbotVisible, setChatbotVisible] = useState(false);
  const [loadingVisible, setLoadingVisible] = useState(false);
  const [loadingText, setLoadingText] = useState("Analyzing Map Data...");
  const [legendVisible, setLegendVisible] = useState(false);
  const [radiusModalVisible, setRadiusModalVisible] = useState(false);
  const [clearModalVisible, setClearModalVisible] = useState(false);
  const [dashboardVisible, setDashboardVisible] = useState(false);
  const [dashboardActivatorVisible, setDashboardActivatorVisible] =
    useState(false);
  const [analysisJsonData, setAnalysisJsonData] = useState(null);

  const [chatMessages, setChatMessages] = useState([
    {
      text: "Hello! I'm your AI assistant. Ask me anything about urban planning, zoning laws, or spatial analysis.",
      type: "ai-message",
    },
  ]);
  const [conversationHistory, setConversationHistory] = useState([]);

  const [hasDrawings, setHasDrawings] = useState(false);
  const [history, setHistory] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const mapDataRef = useRef(null);
  const currentDrawerRef = useRef(null);
  const radiusCenterRef = useRef(null);
  const searchMarkerRef = useRef(null);
  const currentToolRef = useRef(null);

  // Map ready callback
  const handleMapReady = useCallback((mapData) => {
    mapDataRef.current = mapData;
    console.log("Map is ready!", mapData);
  }, []);

  // Handle search
  const handleSearch = async (query) => {
    if (!mapDataRef.current) return;

    const { map } = mapDataRef.current;

    // Remove previous search marker
    if (searchMarkerRef.current) {
      map.removeLayer(searchMarkerRef.current);
      searchMarkerRef.current = null;
    }

    const coordRegex = /^(-?\d{1,3}(\.\d+)?),\s*(-?\d{1,3}(\.\d+)?)$/;
    const match = query.match(coordRegex);

    if (match) {
      const lat = parseFloat(match[1]);
      const lng = parseFloat(match[3]);
      if (lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        map.flyTo([lat, lng], 16);
        searchMarkerRef.current = L.marker([lat, lng])
          .addTo(map)
          .bindPopup(`Lat: ${lat.toFixed(5)}, Lng: ${lng.toFixed(5)}`)
          .openPopup();
      } else {
        alert("Invalid coordinates provided.");
      }
    } else {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        query
      )}`;
      try {
        const response = await fetch(url);
        const data = await response.json();
        if (data && data.length > 0) {
          const { lat, lon, display_name } = data[0];
          map.flyTo([lat, lon], 16);
          searchMarkerRef.current = L.marker([lat, lon])
            .addTo(map)
            .bindPopup(`<b>${display_name}</b>`)
            .openPopup();
        } else {
          alert("Location not found.");
        }
      } catch (error) {
        console.error("Error during geocoding search:", error);
        alert("An error occurred while searching.");
      }
    }
  };

  // Handle current location
  const handleCurrentLocation = () => {
    if (!mapDataRef.current) return;

    const { map } = mapDataRef.current;

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          map.flyTo([lat, lng], 16);
          L.marker([lat, lng])
            .addTo(map)
            .bindPopup("You are here.")
            .openPopup();
        },
        (error) => {
          console.error("Error getting current location:", error.message);
          alert(
            "Could not retrieve your location. Please ensure location services are enabled."
          );
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
      alert("Geolocation is not supported by this browser.");
    }
  };

  // Handle drawing tool selection
  const handleToolSelect = (toolName) => {
    if (!mapDataRef.current) return;

    const { map } = mapDataRef.current;

    // Deactivate current tool
    if (currentDrawerRef.current) {
      currentDrawerRef.current.disable();
      currentDrawerRef.current = null;
    }

    currentToolRef.current = toolName;
    setToolbarVisible(false);

    switch (toolName) {
      case "polygon":
        currentDrawerRef.current = new L.Draw.Polygon(map, {
          shapeOptions: { color: "#007bff" },
        });
        currentDrawerRef.current.enable();
        break;
      case "circle":
        currentDrawerRef.current = new L.Draw.Circle(map, {
          shapeOptions: { color: "#4caf50" },
        });
        currentDrawerRef.current.enable();
        break;
      case "rectangle":
        currentDrawerRef.current = new L.Draw.Rectangle(map, {
          shapeOptions: { color: "#ffc107" },
        });
        currentDrawerRef.current.enable();
        break;
      case "triangle":
        currentDrawerRef.current = new L.Draw.Polygon(map, {
          shapeOptions: { color: "#9c27b0" },
          maxPoints: 3,
        });
        currentDrawerRef.current.enable();
        break;
      case "radius":
        map.getContainer().style.cursor = "pointer";
        map.once("click", (e) => {
          radiusCenterRef.current = e.latlng;
          setRadiusModalVisible(true);
        });
        break;
      default:
        break;
    }
  };

  // Handle radius confirmation
  const handleRadiusConfirm = (radius) => {
    if (radiusCenterRef.current && radius > 0 && mapDataRef.current) {
      const { drawnItems } = mapDataRef.current;
      const circle = L.circle(radiusCenterRef.current, {
        radius,
        color: "#f03",
      });
      drawnItems.addLayer(circle);
      handleDrawCreated(circle);
    }
    setRadiusModalVisible(false);
    radiusCenterRef.current = null;
  };

  // Handle draw created
  const handleDrawCreated = (layer) => {
    setHasDrawings(true);

    // Log bounding box
    const bounds = layer.getBounds();
    const northWest = bounds.getNorthWest();
    const northEast = bounds.getNorthEast();
    const southWest = bounds.getSouthWest();
    const southEast = bounds.getSouthEast();

    console.log("--- Bounding Box Coordinates for New Shape ---");
    console.log(
      `Top-Left (North-West):     Lat: ${northWest.lat.toFixed(
        6
      )}, Lng: ${northWest.lng.toFixed(6)}`
    );
    console.log(
      `Top-Right (North-East):    Lat: ${northEast.lat.toFixed(
        6
      )}, Lng: ${northEast.lng.toFixed(6)}`
    );
    console.log(
      `Bottom-Left (South-West):  Lat: ${southWest.lat.toFixed(
        6
      )}, Lng: ${southWest.lng.toFixed(6)}`
    );
    console.log(
      `Bottom-Right (South-East): Lat: ${southEast.lat.toFixed(
        6
      )}, Lng: ${southEast.lng.toFixed(6)}`
    );
    console.log("--------------------------------------------");
  };

  // Handle analyze
  const handleAnalyze = async () => {
    if (!mapDataRef.current) return;

    const { drawnItems, analysisLayers } = mapDataRef.current;
    const drawnLayers = drawnItems.getLayers();

    if (drawnLayers.length === 0) return;

    // Clear previous analysis
    setConversationHistory([]);
    setChatMessages([]);
    setLoadingVisible(true);
    setLoadingText("Collecting comprehensive area data...");
    analysisLayers.clearLayers();
    setLegendVisible(false);

    try {
      const allPromises = drawnLayers.map(async (userLayer) => {
        const polyFilter = getPolygonFilter(userLayer);

        setLoadingText("Fetching map features, soil & weather data...");

        const [osmData, environmentalData] = await Promise.all([
          fetchOSMData(polyFilter),
          collectAreaData(userLayer, 2),
        ]);

        return {
          userLayer,
          osmData,
          environmentalData,
        };
      });

      const results = await Promise.all(allPromises);

      let featuresFound = false;
      let comprehensiveData = {
        areas: [],
        collection_timestamp: new Date().toISOString(),
      };

      results.forEach((result) => {
        if (!result) return;

        const { userLayer, osmData, environmentalData } = result;
        const userLayerGeoJSON = userLayer.toGeoJSON();

        // Process and visualize OSM data
        osmData.elements.forEach((element) => {
          if (element.type === "way" && element.geometry) {
            const latlngs = element.geometry.map((pt) => [pt.lat, pt.lon]);
            const osmFeature = L.polygon(latlngs).toGeoJSON();

            try {
              const intersection = turf.intersect(userLayerGeoJSON, osmFeature);

              if (intersection) {
                let style = null;

                if (element.tags.building) {
                  style = {
                    fillColor: "#FFA500",
                    color: "#D2691E",
                    weight: 1,
                    fillOpacity: 0.5,
                  };
                } else if (
                  element.tags.natural === "wood" ||
                  element.tags.leisure === "park"
                ) {
                  style = {
                    fillColor: "#228B22",
                    color: "#006400",
                    weight: 1,
                    fillOpacity: 0.5,
                  };
                } else if (element.tags.natural === "water") {
                  style = {
                    fillColor: "#4682B4",
                    color: "#1E90FF",
                    weight: 1,
                    fillOpacity: 0.6,
                  };
                } else if (element.tags.landuse === "residential") {
                  style = {
                    fillColor: "#FFC0CB",
                    color: "#FFB6C1",
                    weight: 1,
                    fillOpacity: 0.5,
                  };
                } else if (element.tags.landuse === "commercial") {
                  style = {
                    fillColor: "#DA70D6",
                    color: "#BA55D3",
                    weight: 1,
                    fillOpacity: 0.5,
                  };
                } else if (element.tags.landuse === "industrial") {
                  style = {
                    fillColor: "#808080",
                    color: "#696969",
                    weight: 1,
                    fillOpacity: 0.5,
                  };
                }

                if (style) {
                  featuresFound = true;
                  L.geoJSON(intersection, { style: style }).addTo(
                    analysisLayers
                  );
                }
              }
            } catch (e) {
              console.error("Error with Turf.js intersection:", e);
            }
          }
        });

        comprehensiveData.areas.push({
          osm_features: osmData,
          environmental_data: environmentalData,
        });
      });

      // Send to GPT for analysis
      if (comprehensiveData.areas.length > 0) {
        setLoadingText("Analyzing data with AI...");

        const analysisResult = await analyzeWithGPT(
          comprehensiveData,
          conversationHistory
        );
        console.log("=== GPT Analysis Results ===");
        console.log("Formatted Analysis:");
        console.log(analysisResult.formatted);
        console.log("\n=== Structured JSON Data ===");

        // Log the JSON in multiple formats for easy access
        if (analysisResult.json) {
          console.log("📊 JSON Object (click to expand):", analysisResult.json);
          console.log("📋 JSON String (copy-paste ready):");
          console.log(JSON.stringify(analysisResult.json, null, 2));
        } else {
          console.warn("⚠️ No JSON data returned. Raw response:");
          console.log(analysisResult.raw);
        }

        console.log("===========================");

        // Also save to window for easy access in DevTools
        window.latestAnalysisJSON = analysisResult.json;
        window.latestAnalysisRaw = analysisResult.raw;
        console.log(
          "💡 Tip: Access JSON via 'window.latestAnalysisJSON' or raw response via 'window.latestAnalysisRaw'"
        );

        // Update conversation history
        setConversationHistory(analysisResult.conversationHistory);

        // Save analysis JSON data for dashboard
        setAnalysisJsonData(analysisResult.json);

        // Clear chat and add analysis
        const newMessages = [];
        newMessages.push({
          text: "Area Analysis Complete",
          type: "ai-message",
        });

        // Add bounding box info
        const firstArea = comprehensiveData.areas[0];
        if (firstArea?.environmental_data?.bounding_box) {
          const bbox = firstArea.environmental_data.bounding_box;
          newMessages.push({
            text: `Analysis Area: North ${bbox.north.toFixed(
              6
            )}, South ${bbox.south.toFixed(6)}, East ${bbox.east.toFixed(
              6
            )}, West ${bbox.west.toFixed(6)}`,
            type: "ai-message",
          });
        }

        newMessages.push({
          text: analysisResult.formatted,
          type: "ai-message",
        });

        // Add prompt about dashboard opening
        if (analysisResult.json) {
          newMessages.push({
            text: "📊 Analysis Dashboard opening automatically! Check the bottom-left corner for detailed visual insights. You can use the chatbot while viewing the dashboard.",
            type: "ai-message",
          });

          // Add helpful suggestions for follow-up questions
          newMessages.push({
            text: "💬 I'm here to help! You can ask me:\n• What are the next steps for this area?\n• Can you explain the risks in detail?\n• What should I prioritize first?\n• How can I improve the development suitability?\n• Any questions about the analysis results!",
            type: "ai-message",
          });
        }

        setChatMessages(newMessages);

        // Open chatbot
        setChatbotVisible(true);

        // Hide toolbox if it's open and auto-open dashboard
        if (analysisResult.json) {
          setToolbarVisible(false);
          setDashboardActivatorVisible(true);
          // Auto-open dashboard after a short delay
          setTimeout(() => {
            setDashboardVisible(true);
          }, 1500);
        }
      }

      setLoadingVisible(false);
      if (featuresFound) {
        setLegendVisible(true);
      } else {
        console.warn("No analyzable features found.");
      }
    } catch (error) {
      console.error("Error in analysis:", error);
      setChatMessages([
        { text: "Error in analysis: " + error.message, type: "ai-message" },
      ]);
      setChatbotVisible(true);
      setLoadingVisible(false);
    }
  };

  // Handle chat message
  const handleChatMessage = async (message) => {
    // Add user message
    setChatMessages((prev) => [
      ...prev,
      { text: message, type: "user-message" },
    ]);

    // Add loading indicator
    setChatMessages((prev) => [
      ...prev,
      { text: "...", type: "loading-indicator" },
    ]);

    try {
      // Pass analysis data if available so the agent has context
      const result = await askGPT(
        message,
        conversationHistory,
        analysisJsonData
      );

      // Remove loading indicator and add AI response
      setChatMessages((prev) => {
        const filtered = prev.filter((msg) => msg.type !== "loading-indicator");
        return [...filtered, { text: result.response, type: "ai-message" }];
      });

      setConversationHistory(result.conversationHistory);
    } catch (error) {
      setChatMessages((prev) => {
        const filtered = prev.filter((msg) => msg.type !== "loading-indicator");
        return [
          ...filtered,
          { text: "Error: " + error.message, type: "ai-message" },
        ];
      });
    }
  };

  // Handle clear all
  const handleClearAll = () => {
    if (!mapDataRef.current) return;

    const { drawnItems, analysisLayers } = mapDataRef.current;
    drawnItems.clearLayers();
    analysisLayers.clearLayers();
    setHasDrawings(false);
    setHistory([]);
    setRedoStack([]);
    setLegendVisible(false);
    setDashboardActivatorVisible(false);
    setDashboardVisible(false);
    setAnalysisJsonData(null);
    setClearModalVisible(false);
  };

  return (
    <div className="app-container">
      <MapComponent
        activeLayer={activeLayer}
        onMapReady={handleMapReady}
        onDrawCreated={handleDrawCreated}
      />

      <TopBar onSearch={handleSearch} />

      <LayerControl
        activeLayer={activeLayer}
        onLayerChange={setActiveLayer}
        onCurrentLocation={handleCurrentLocation}
      />

      <ActivatorIcons
        onToolbarToggle={() => setToolbarVisible(!toolbarVisible)}
        onChatbotToggle={() => setChatbotVisible(true)}
        dashboardVisible={dashboardActivatorVisible}
        onDashboardToggle={() => setDashboardVisible(!dashboardVisible)}
      />

      <Toolbar
        isVisible={toolbarVisible}
        hasDrawings={hasDrawings}
        historyLength={history.length}
        redoStackLength={redoStack.length}
        onToolSelect={handleToolSelect}
        onAnalyze={handleAnalyze}
        onUndo={() => {
          /* TODO: implement */
        }}
        onRedo={() => {
          /* TODO: implement */
        }}
        onClearAll={() => setClearModalVisible(true)}
      />

      <Chatbot
        isVisible={chatbotVisible}
        onClose={() => setChatbotVisible(false)}
        messages={chatMessages}
        onSendMessage={handleChatMessage}
      />

      <LoadingOverlay isVisible={loadingVisible} text={loadingText} />

      <AnalysisLegend
        isVisible={legendVisible}
        onClose={() => setLegendVisible(false)}
      />

      <AnalysisDashboard
        isVisible={dashboardVisible}
        onClose={() => {
          setDashboardVisible(false);
          // Keep dashboard activator visible when closing dashboard
        }}
        analysisData={analysisJsonData}
      />

      <RadiusModal
        isVisible={radiusModalVisible}
        onConfirm={handleRadiusConfirm}
        onCancel={() => {
          setRadiusModalVisible(false);
          radiusCenterRef.current = null;
        }}
      />

      <ClearModal
        isVisible={clearModalVisible}
        onConfirm={handleClearAll}
        onCancel={() => setClearModalVisible(false)}
      />
    </div>
  );
}

export default App;
